import { Role } from 'app/pages/UserPage/type';
import type { UserSelectOption } from 'hooks/useGetUser';

type ClientRef = { id?: number | string | bigint | null };
type SubmittedRef = {
  id?: number;
  clientId?: number | string | bigint | null;
  client?: ClientRef | null;
  roomId?: number | string | bigint | null;
  room?: { client?: ClientRef | null; clientId?: number | string | bigint | null } | null;
};

type IssueClientSource = {
  clientId?: number | string | bigint | null;
  client?: ClientRef | null;
  room?: { client?: ClientRef | null; clientId?: number | string | bigint | null } | null;
  submitted?: SubmittedRef | null;
  submittedAsset?: {
    submitted?: SubmittedRef | null;
  } | null;
};

const toClientId = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

/** Resolve client id from ad-hoc, asset, or room issue payloads. */
export const resolveIssueClientId = (issue: unknown): number | undefined => {
  if (!issue || typeof issue !== 'object') return undefined;
  const source = issue as IssueClientSource;

  const direct =
    toClientId(source.client?.id) ??
    toClientId(source.clientId) ??
    toClientId(source.room?.client?.id) ??
    toClientId(source.room?.clientId) ??
    toClientId(source.submitted?.client?.id) ??
    toClientId(source.submitted?.clientId) ??
    toClientId(source.submitted?.room?.client?.id) ??
    toClientId(source.submitted?.room?.clientId) ??
    toClientId(source.submittedAsset?.submitted?.client?.id) ??
    toClientId(source.submittedAsset?.submitted?.clientId) ??
    toClientId(source.submittedAsset?.submitted?.room?.client?.id) ??
    toClientId(source.submittedAsset?.submitted?.room?.clientId);

  return direct;
};

export const resolveIssueNotificationClientId = (
  issue: unknown,
  hints?: { clientId?: number; submitClientId?: number }
): number | undefined => {
  return (
    resolveIssueClientId(issue) ?? toClientId(hints?.clientId) ?? toClientId(hints?.submitClientId)
  );
};

export const filterIssueNotificationUsers = (
  users: UserSelectOption[] | undefined,
  issueClientId: number | undefined
): UserSelectOption[] => {
  if (!Array.isArray(users) || !issueClientId) return [];
  return users.filter((u) => {
    const role = String(u.role || '');
    if (role === Role.SUPER_ADMIN || role === Role.ADMIN) return true;
    const hasClientAccess = (u.accessibleClients || []).some(
      (c) => Number(c.id) === Number(issueClientId)
    );
    if (!hasClientAccess) return false;
    const viewAccess = (u.viewAccess || []).map((p) => String(p).toUpperCase());
    const editAccess = (u.editAccess || []).map((p) => String(p).toUpperCase());
    return viewAccess.includes('ISSUES') || editAccess.includes('ISSUES');
  });
};
